import { useMemo, useState } from 'react'
import { CheckCircle2, ChevronDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Card, CardContent, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { formatEnumValue } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import type {
  QueueHealth,
  QueueJob,
  QueueJobDetail,
} from '@/features/chronograph-dashboard/types'
import { useSyncJobStatus } from '@/hooks/use-chronograph'
import {
  computeDuration,
  countSkipIssues,
  formatFieldLabel,
  formatJobRelativeTime,
  formatParameterValue,
  formatRelativeTime,
  formatSyncTypeLabel,
  getProgressPercent,
  getSkipSeverity,
  getStringArray,
  hasJobDetail,
  isLiveJobState,
  isPrimitiveValue,
  isRecord,
  ProgressMeter,
  sortSkipsBySeverity,
  StatusBadge,
  type SkipSeverity,
} from '@/components/custom/chronograph/shared'

const HIDDEN_PARAM_KEYS = new Set(['type', 'trigger', 'userId'])

interface QueueMonitorTabProps {
  queueHealth: QueueHealth | undefined
  recentJobs: QueueJob[]
  isStatusLoading: boolean
}

export function QueueMonitorTab({
  queueHealth,
  recentJobs,
  isStatusLoading,
}: QueueMonitorTabProps) {
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null)

  const liveJobs = useMemo(
    () => recentJobs.filter((job) => isLiveJobState(job.state)),
    [recentJobs]
  )

  const effectiveSelectedJobId = useMemo(() => {
    if (selectedJobId && recentJobs.some((job) => job.id === selectedJobId)) {
      return selectedJobId
    }
    return liveJobs[0]?.id ?? recentJobs[0]?.id ?? null
  }, [liveJobs, recentJobs, selectedJobId])

  const selectedJobSummary = useMemo(
    () => recentJobs.find((job) => job.id === effectiveSelectedJobId) ?? null,
    [effectiveSelectedJobId, recentJobs]
  )

  const selectedJobDetail = useSyncJobStatus(effectiveSelectedJobId)
  const selectedJob = selectedJobDetail.data ?? selectedJobSummary ?? null

  const queueSummary = queueHealth
    ? `${queueHealth.failed} failed \u2022 ${queueHealth.delayed} delayed`
    : 'Queue unavailable'

  if (isStatusLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!queueHealth) {
    return (
      <div className="rounded-xl border border-dashed border-primary/15 px-4 py-6 text-center">
        <Typography variant="body-sm" className="text-primary/70">
          Queue health is currently unavailable.
        </Typography>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant="outline"
          className="border-primary/15 bg-background font-inter tabular-nums"
        >
          {recentJobs.length} jobs
        </Badge>
        <Typography variant="body-sm" className="text-primary/70">
          {liveJobs.length > 0
            ? `${liveJobs.length} running or queued now`
            : queueSummary}
        </Typography>
      </div>

      {/* Queue Health — compact when idle, expanded when something's happening */}
      <CompactQueueHealth queueHealth={queueHealth} />

      {/* 2-column layout: job list + detail */}
      {recentJobs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-primary/15 px-4 py-6 text-center">
          <Typography variant="body-sm" className="text-primary/70">
            No recent queue jobs yet.
          </Typography>
        </div>
      ) : (
        <div className="flex gap-4">
          {/* Job list (left) */}
          <div className="w-[280px] shrink-0 space-y-1.5">
            {recentJobs.map((job) => (
              <button
                type="button"
                key={job.id}
                onClick={() => setSelectedJobId(job.id)}
                className={cn(
                  'flex w-full cursor-pointer flex-col gap-1 rounded-lg border px-3 py-2.5 text-left transition-colors',
                  effectiveSelectedJobId === job.id
                    ? 'border-primary/25 bg-primary/[0.04]'
                    : 'border-primary/10 bg-background hover:bg-primary/[0.02]'
                )}
              >
                <div className="flex items-center gap-2">
                  <Typography
                    variant="body-sm"
                    className="font-mono text-primary/70"
                  >
                    #{job.id}
                  </Typography>
                  <StatusBadge status={job.state} />
                </div>
                <Typography variant="body-sm" className="text-primary/70">
                  {formatSyncTypeLabel(job.type)}
                </Typography>
                <Typography variant="body-sm" className="text-primary/70">
                  {formatJobRelativeTime(job)}
                </Typography>
              </button>
            ))}
          </div>

          {/* Detail panel (right) */}
          <div className="min-w-0 flex-1">
            {selectedJobDetail.isError ? (
              <Card className="border-destructive/20 shadow-none">
                <CardContent className="flex h-full flex-col items-center justify-center gap-3 px-6 py-8 text-center">
                  <Typography
                    variant="small"
                    className="font-medium text-destructive"
                  >
                    Failed to load selected job details.
                  </Typography>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => selectedJobDetail.refetch()}
                  >
                    Retry
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <QueueJobDetailsPanel
                job={selectedJob}
                isLoading={selectedJobDetail.isLoading}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Sub-components
// =============================================================================

function CompactQueueHealth({ queueHealth }: { queueHealth: QueueHealth }) {
  const hasActivity =
    queueHealth.waiting > 0 ||
    queueHealth.active > 0 ||
    queueHealth.failed > 0 ||
    queueHealth.delayed > 0

  const states = [
    'waiting',
    'active',
    'completed',
    'failed',
    'delayed',
  ] as const

  // When nothing is happening, show a single compact row
  if (!hasActivity) {
    return (
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-primary/10 bg-primary/[0.015] px-4 py-2.5">
        <Typography variant="body-sm" className="font-medium text-primary/70">
          Queue
        </Typography>
        {states.map((state) => (
          <div key={state} className="flex items-center gap-1.5">
            <Typography
              variant="body-sm"
              className="capitalize text-primary/70"
            >
              {state}:
            </Typography>
            <Typography
              variant="body-sm"
              className="font-inter font-medium tabular-nums text-primary/70"
            >
              {queueHealth[state]}
            </Typography>
          </div>
        ))}
      </div>
    )
  }

  // When there's activity, expand into a more prominent display
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
      {states.map((state) => (
        <div
          key={state}
          className={cn(
            'rounded-lg border px-3 py-3 text-center',
            state === 'failed' && queueHealth[state] > 0
              ? 'border-destructive/20 bg-destructive/[0.03]'
              : state === 'active' && queueHealth[state] > 0
                ? 'border-green-600/20 bg-green-600/[0.03]'
                : 'border-primary/10 bg-primary/[0.015]'
          )}
        >
          <Typography
            variant="metric-value"
            className={cn(
              state === 'failed' &&
                queueHealth[state] > 0 &&
                'text-destructive',
              state === 'active' && queueHealth[state] > 0 && 'text-green-600'
            )}
          >
            {queueHealth[state]}
          </Typography>
          <Typography
            variant="body-sm"
            className="mt-1 capitalize text-primary/70"
          >
            {state}
          </Typography>
        </div>
      ))}
    </div>
  )
}

function QueueJobDetailsPanel({
  job,
  isLoading,
}: {
  job: QueueJob | QueueJobDetail | null
  isLoading: boolean
}) {
  if (!job) {
    return (
      <Card className="border-primary/15 shadow-none">
        <CardContent className="flex h-full items-center justify-center px-6 py-8 text-center">
          <Typography variant="body-sm" className="text-primary/70">
            Select a queue job to inspect its progress and result payload.
          </Typography>
        </CardContent>
      </Card>
    )
  }

  const progressPercent = getProgressPercent(job.progress)
  const live = isLiveJobState(job.state)
  const duration = computeDuration(job.processedAt, job.finishedAt)
  const parameters = hasJobDetail(job)
    ? Object.entries(job.data).filter(
        ([key, value]) => !HIDDEN_PARAM_KEYS.has(key) && isPrimitiveValue(value)
      )
    : []

  return (
    <Card className="overflow-hidden border-primary/20 shadow-none">
      {/* Colored header bar */}
      <div className="border-b border-primary/10 bg-primary/[0.03] px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="font-mono text-xl font-semibold text-primary">
              #{job.id}
            </CardTitle>
            <Typography variant="body-sm" className="mt-1.5 text-primary/70">
              {formatSyncTypeLabel(job.type)} job &bull;{' '}
              {formatJobRelativeTime(job)}
            </Typography>
          </div>
          <StatusBadge status={job.state} />
        </div>

        {/* Inline stats as pills */}
        <div className="mt-3 flex flex-wrap gap-2">
          <StatPill
            label="Duration"
            value={duration ?? (live ? 'Running...' : '\u2014')}
          />
          <StatPill
            label="Triggered"
            value={formatRelativeTime(job.createdAt)}
          />
          {progressPercent !== null ? (
            <StatPill label="Progress" value={`${progressPercent}%`} />
          ) : null}
        </div>
      </div>

      <CardContent className="space-y-5 px-5 pt-5">
        {/* Progress */}
        <div>
          <div className="flex items-baseline justify-between gap-3">
            <Typography
              variant="body-sm"
              className="font-medium text-primary/70"
            >
              {job.progress?.step ?? 'No active step reported'}
            </Typography>
            <Typography
              variant="body-sm"
              className="shrink-0 font-inter text-[11px] tabular-nums text-primary/70"
            >
              {progressPercent !== null
                ? `${progressPercent}%`
                : live
                  ? 'live'
                  : ''}
            </Typography>
          </div>
          {job.progress?.detail ? (
            <Typography variant="body-sm" className="mt-0.5 text-primary/70">
              {job.progress.detail}
            </Typography>
          ) : null}
          <ProgressMeter value={progressPercent} live={live} className="mt-2" />
        </div>

        {/* Parameters */}
        {parameters.length > 0 ? (
          <div className="flex flex-wrap gap-2 border-t border-primary/10 pt-4">
            {parameters.map(([key, value]) => (
              <StatPill
                key={key}
                label={formatFieldLabel(key)}
                value={formatParameterValue(value)}
              />
            ))}
          </div>
        ) : null}

        {/* Failure reason */}
        {hasJobDetail(job) && job.failedReason ? (
          <div className="rounded-lg border border-destructive/15 bg-destructive/[0.03] px-4 py-3">
            <Typography
              variant="body-sm"
              className="font-semibold text-destructive/80"
            >
              Failure reason
            </Typography>
            <Typography variant="body-sm" className="mt-1.5 text-primary/70">
              {job.failedReason}
            </Typography>
          </div>
        ) : null}

        {/* Result */}
        {isLoading && !hasJobDetail(job) ? (
          <div className="space-y-2">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : hasJobDetail(job) && job.result ? (
          <JobResultSummary result={job.result} />
        ) : (
          <Typography variant="body-sm" className="pt-2 text-primary/70">
            {live
              ? 'Results will appear when the job completes.'
              : 'No result payload stored.'}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

function StatPill({ label, value }: { label: string; value: string }) {
  return (
    <div className="inline-flex items-baseline gap-1.5 rounded-md border border-primary/10 bg-background px-2.5 py-1">
      <Typography variant="body-sm" className="text-[11px] text-primary/70">
        {label}
      </Typography>
      <Typography
        variant="body-sm"
        className="font-inter text-xs font-semibold tabular-nums text-primary/80"
      >
        {value}
      </Typography>
    </div>
  )
}

// =============================================================================
// Job Result Components
// =============================================================================

interface StandardResultSection extends Record<string, unknown> {
  created: number
  updated: number
  skipped: number
  duration?: string
  errors?: unknown
  skipBreakdown?: unknown
}

interface MetricResultSection extends Record<string, unknown> {
  totalCreated: number
  totalUpdated: number
  totalSkipped: number
  byYear?: Record<string, unknown>
}

function isStandardResultSection(
  value: Record<string, unknown>
): value is StandardResultSection {
  return (
    typeof value.created === 'number' &&
    typeof value.updated === 'number' &&
    typeof value.skipped === 'number'
  )
}

function isMetricResultSection(
  value: Record<string, unknown>
): value is MetricResultSection {
  return (
    typeof value.totalCreated === 'number' &&
    typeof value.totalUpdated === 'number' &&
    typeof value.totalSkipped === 'number'
  )
}

function StatDot({
  label,
  value,
  color,
}: {
  label: string
  value: number
  color: 'green' | 'blue' | 'muted'
}) {
  const dotColor =
    value === 0
      ? 'bg-primary/15'
      : color === 'green'
        ? 'bg-emerald-500'
        : color === 'blue'
          ? 'bg-blue-500'
          : 'bg-primary/30'

  return (
    <div className="flex items-center gap-1.5">
      <div className={cn('size-2 rounded-full', dotColor)} />
      <Typography
        variant="body-sm"
        className="font-inter text-[13px] tabular-nums font-semibold text-primary/80"
      >
        {value.toLocaleString()}
      </Typography>
      <Typography variant="body-sm" className="text-xs text-primary/70">
        {label}
      </Typography>
    </div>
  )
}

function JobResultSummary({ result }: { result: Record<string, unknown> }) {
  const errors = getStringArray(result.errors)
  const sections = Object.entries(result).filter(
    (entry): entry is [string, Record<string, unknown>] => isRecord(entry[1])
  )

  return (
    <div className="space-y-3">
      {sections.map(([key, value]) => (
        <JobResultSection
          key={key}
          title={formatFieldLabel(key)}
          value={value}
        />
      ))}

      {errors.length > 0 ? (
        <div className="rounded-lg border border-destructive/15 bg-destructive/[0.03] px-4 py-3">
          <Typography
            variant="body-sm"
            className="font-semibold text-destructive"
          >
            Errors
          </Typography>
          <div className="mt-1.5 space-y-1">
            {errors.map((error, index) => (
              <Typography
                key={`${index}-${error}`}
                variant="body-sm"
                className="text-primary/70"
              >
                &bull; {error}
              </Typography>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function JobResultSection({
  title,
  value,
}: {
  title: string
  value: Record<string, unknown>
}) {
  const sectionErrors = getStringArray(value.errors)
  const skipBreakdown = Array.isArray(value.skipBreakdown)
    ? value.skipBreakdown.filter(isRecord)
    : []

  if (isStandardResultSection(value)) {
    const typedBreakdown = skipBreakdown.map((item) => ({
      reason: typeof item.reason === 'string' ? item.reason : 'UNKNOWN',
      count: typeof item.count === 'number' ? item.count : 0,
    }))
    const totalSkipCount = typedBreakdown.reduce(
      (sum, item) => sum + item.count,
      0
    )
    const issueCount = countSkipIssues(typedBreakdown)
    const sortedBreakdown = sortSkipsBySeverity(typedBreakdown)
    const hasRealWork =
      value.created > 0 ||
      value.updated > 0 ||
      sectionErrors.length > 0 ||
      issueCount > 0
    const allHealthy =
      skipBreakdown.length > 0 && issueCount === 0 && !hasRealWork

    // No-op section: collapse by default when nothing interesting happened
    if (allHealthy) {
      return (
        <NoOpSection
          title={title}
          skipped={value.skipped}
          duration={
            typeof value.duration === 'string' ? value.duration : undefined
          }
          sortedBreakdown={sortedBreakdown}
          sectionTitle={title}
        />
      )
    }

    return (
      <div
        className={cn(
          'rounded-lg border px-4 py-3.5',
          issueCount > 0 || sectionErrors.length > 0
            ? 'border-amber-500/20 bg-amber-500/[0.02]'
            : 'border-primary/12 bg-primary/[0.02]'
        )}
      >
        {/* Title + duration */}
        <div className="flex items-baseline justify-between">
          <Typography
            variant="small"
            className="text-[13px] font-semibold text-primary"
          >
            {title}
          </Typography>
          {typeof value.duration === 'string' ? (
            <Typography
              variant="body-sm"
              className="font-inter text-[11px] tabular-nums text-primary/70"
            >
              {value.duration}
            </Typography>
          ) : null}
        </div>

        {/* Stat indicators */}
        <div className="mt-2.5 flex gap-4">
          <StatDot label="created" value={value.created} color="green" />
          <StatDot label="updated" value={value.updated} color="blue" />
          <StatDot label="skipped" value={value.skipped} color="muted" />
        </div>

        {/* Skip breakdown with severity colors */}
        {sortedBreakdown.length > 0 ? (
          <div className="mt-3 rounded-md bg-primary/[0.03] px-3 py-2.5">
            <div className="mb-2 flex items-center justify-between">
              <Typography
                variant="body-sm"
                className="text-[11px] font-semibold uppercase tracking-wide text-primary/70"
              >
                Skip reasons
              </Typography>
              <div className="flex items-center gap-1.5">
                {issueCount > 0 ? (
                  <Badge
                    variant="outline"
                    className="h-5 border-amber-500/25 bg-amber-500/[0.06] px-1.5 font-inter text-[10px] tabular-nums text-amber-700"
                  >
                    {issueCount.toLocaleString()}{' '}
                    {issueCount === 1 ? 'issue' : 'issues'}
                  </Badge>
                ) : null}
                <Badge
                  variant="outline"
                  className="h-5 border-primary/15 px-1.5 font-inter text-[10px] tabular-nums"
                >
                  {totalSkipCount.toLocaleString()} total
                </Badge>
              </div>
            </div>
            <div className="space-y-1">
              {sortedBreakdown.map((item, index) => (
                <SkipReasonRow
                  key={`${title}-skip-${index}`}
                  reason={item.reason}
                  count={item.count}
                />
              ))}
            </div>
          </div>
        ) : null}

        {/* Section errors */}
        {sectionErrors.length > 0 ? (
          <div className="mt-2.5 space-y-0.5">
            {sectionErrors.map((error, index) => (
              <Typography
                key={`${title}-error-${index}`}
                variant="body-sm"
                className="text-destructive/70"
              >
                &bull; {error}
              </Typography>
            ))}
          </div>
        ) : null}
      </div>
    )
  }

  if (isMetricResultSection(value)) {
    const byYear = isRecord(value.byYear) ? value.byYear : {}

    return (
      <div className="rounded-lg border border-primary/12 bg-primary/[0.02] px-4 py-3.5">
        <Typography
          variant="small"
          className="text-[13px] font-semibold text-primary"
        >
          {title}
        </Typography>
        <div className="mt-2.5 flex gap-4">
          <StatDot label="created" value={value.totalCreated} color="green" />
          <StatDot label="updated" value={value.totalUpdated} color="blue" />
          <StatDot label="skipped" value={value.totalSkipped} color="muted" />
        </div>
        {Object.keys(byYear).length > 0 ? (
          <div className="mt-3">
            <YearBreakdown byYear={byYear} />
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-primary/12 bg-primary/[0.02] px-4 py-3.5">
      <Typography
        variant="small"
        className="text-[13px] font-semibold text-primary"
      >
        {title}
      </Typography>
      <pre className="mt-2.5 overflow-x-auto rounded-md bg-primary/[0.04] p-3 text-xs text-primary/70">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  )
}

function YearBreakdown({ byYear }: { byYear: Record<string, unknown> }) {
  const entries = Object.entries(byYear).filter(
    ([, value]) => typeof value === 'number'
  ) as Array<[string, number]>

  if (entries.length === 0) return null

  const maxValue = Math.max(...entries.map(([, value]) => value), 1)

  return (
    <div className="space-y-1">
      {entries.map(([year, value]) => (
        <div key={year} className="flex items-center gap-3">
          <Typography
            variant="body-sm"
            className="w-10 shrink-0 text-xs tabular-nums text-primary/70"
          >
            {year}
          </Typography>
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-primary/[0.06]">
            <div
              className="h-full rounded-full bg-primary/50"
              style={{ width: `${(value / maxValue) * 100}%` }}
            />
          </div>
          <Typography
            variant="body-sm"
            className="w-12 shrink-0 text-right font-inter text-xs tabular-nums text-primary/70"
          >
            {value.toLocaleString()}
          </Typography>
        </div>
      ))}
    </div>
  )
}

// =============================================================================
// Skip Reason Components
// =============================================================================

const SEVERITY_STYLES: Record<
  SkipSeverity,
  { text: string; count: string; dot: string }
> = {
  error: {
    text: 'text-destructive/80',
    count: 'font-semibold text-destructive/70',
    dot: 'bg-destructive',
  },
  warning: {
    text: 'text-amber-700/80',
    count: 'font-semibold text-amber-700/70',
    dot: 'bg-amber-500',
  },
  healthy: {
    text: 'text-primary/70',
    count: 'text-primary/70',
    dot: 'bg-primary/20',
  },
}

function SkipReasonRow({ reason, count }: { reason: string; count: number }) {
  const severity = getSkipSeverity(reason)
  const styles = SEVERITY_STYLES[severity]

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-1.5">
        <div className={cn('size-1.5 shrink-0 rounded-full', styles.dot)} />
        <Typography variant="body-sm" className={cn('text-xs', styles.text)}>
          {formatEnumValue(reason)}
        </Typography>
      </div>
      <Typography
        variant="body-sm"
        className={cn('font-inter text-xs tabular-nums', styles.count)}
      >
        {count.toLocaleString()}
      </Typography>
    </div>
  )
}

function NoOpSection({
  title,
  skipped,
  duration,
  sortedBreakdown,
  sectionTitle,
}: {
  title: string
  skipped: number
  duration?: string
  sortedBreakdown: Array<{ reason: string; count: number }>
  sectionTitle: string
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div className="rounded-lg border border-primary/8 bg-primary/[0.01]">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-2.5 text-left"
          >
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-3.5 text-primary/25" />
              <Typography
                variant="body-sm"
                className="text-[13px] text-primary/70"
              >
                {title}
              </Typography>
              <Typography variant="body-sm" className="text-xs text-primary/70">
                {skipped.toLocaleString()} skipped, all synced
              </Typography>
            </div>
            <div className="flex items-center gap-2">
              {duration ? (
                <Typography
                  variant="body-sm"
                  className="font-inter text-[11px] tabular-nums text-primary/70"
                >
                  {duration}
                </Typography>
              ) : null}
              <ChevronDown
                className={cn(
                  'size-3.5 text-primary/70 transition-transform duration-200',
                  isExpanded && 'rotate-180'
                )}
              />
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-primary/8 px-4 py-3">
            <div className="flex gap-4">
              <StatDot label="created" value={0} color="green" />
              <StatDot label="updated" value={0} color="blue" />
              <StatDot label="skipped" value={skipped} color="muted" />
            </div>
            {sortedBreakdown.length > 0 ? (
              <div className="mt-2.5 space-y-1">
                {sortedBreakdown.map((item, index) => (
                  <SkipReasonRow
                    key={`${sectionTitle}-skip-${index}`}
                    reason={item.reason}
                    count={item.count}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
